/**
 * terrain-sprites.ts — v4M13.1 — per-kind commissioned bitmap sprite
 * cache + loader for terrain features (`EnvironmentEntity`).
 *
 * Architectural twin of `space-structure-sprites.ts` (v4M4.b),
 * `ship-sprites.ts` (v4M4.a), and `backdrop.ts` (v4M1.8a):
 *   1. A `Map<EnvironmentKind, OffscreenCanvas>` cache keyed by kind
 *      name only (terrain features are fully race-neutral by design).
 *   2. `loadTerrainSpriteImage(kind, url)` fetches an
 *      `HTMLImageElement`, paints into a 512×512 OffscreenCanvas, and
 *      replaces the cache entry. Headless-safe + non-rejecting per
 *      the backdrop pattern — a missing file leaves the cache empty
 *      so the consumer's procedural fallback stays active.
 *   3. `getTerrainSprite(kind)` is the cache read. Returns null when
 *      no sprite is loaded for the kind.
 *
 * The render pipeline (`system-view.ts:drawEnvironmentEntities`)
 * checks `getTerrainSprite(...)`; if non-null it blits the bitmap
 * (scaled to the entity's `radius × 2` diameter, rotated by the
 * deterministic golden-angle stride) instead of running the
 * procedural per-kind silhouette. Procedural draws remain in
 * `system-view.ts` so the game ships fully without commissioned
 * sprites — Alpha 0.6 visual read is preserved verbatim until sprite
 * assets land in `public/assets/terrain/`.
 *
 * Sprite authoring contract (per `TERRAIN_FEATURES.md §2.1`):
 *   - 512×512 PNG, RGBA, transparent background.
 *   - Centred at (256, 256).
 *   - Sprite fills its bounding circle (entity's logical radius);
 *     `system-view.ts` blits at `radius × 2 × zoom × viewW` screen
 *     diameter.
 *   - Race-neutral colouring (terrain features have no per-empire
 *     tint).
 *   - Static base — animation lives in the procedural overlay layer
 *     that v4M13.3 wires on top of the sprite.
 *
 * URL convention: `/assets/terrain/<kind>.png` where `<kind>` is the
 * `EnvironmentKind` enum value used verbatim (already kebab-case in
 * the type union — no transformation). Examples:
 *   - `EnvironmentKind` "asteroid-field" → `/assets/terrain/asteroid-field.png`
 *   - `EnvironmentKind` "black-hole"     → `/assets/terrain/black-hole.png`
 *   - `EnvironmentKind` "exotic-gas-cloud" → `/assets/terrain/exotic-gas-cloud.png`
 *
 * Cache key uses `EnvironmentKind`'s string value directly so test
 * fixtures can stub via `loadTerrainSpriteImage('nebula', ...)`
 * without importing the kind union itself.
 *
 * v4M13.1 strict-parity: scaffolding only. The 6 commissioned PNGs
 * land at v4M13.2; until then the cache stays empty and the
 * procedural per-kind branches in `drawEnvironmentEntities` provide
 * the visual read identical to pre-v4M13.
 */

import type { EnvironmentKind } from '../state/types/index';

/**
 * Canonical sprite dimensions per `TERRAIN_FEATURES.md §2.1`. The
 * 512 px tile suits terrain features' larger on-canvas footprint
 * (~200-800 px screen diameter depending on entity radius + zoom).
 * Larger than structures' 64 px because terrain features render at
 * effective 1.0× scale (sprite-fills-radius) rather than the heavy
 * downscale structures use.
 */
const TERRAIN_SPRITE_SIZE = 512;

/** Cache key (kind) → painted offscreen canvas. */
const terrainSpriteCache: Map<EnvironmentKind, OffscreenCanvas> = new Map();

/**
 * Return the cached sprite for the given kind, or null when no
 * sprite has been loaded for it. Consumers
 * (`system-view.ts:drawEnvironmentEntities`) branch on this — null
 * falls through to the procedural per-kind silhouette.
 */
export function getTerrainSprite(
  kind: EnvironmentKind,
): OffscreenCanvas | null {
  return terrainSpriteCache.get(kind) ?? null;
}

/**
 * v4M13.1 — load a commissioned terrain sprite for the given kind.
 * Painted into a 512×512 OffscreenCanvas regardless of source
 * dimensions; commissioned sprites should be authored at exactly
 * 512×512.
 *
 * Promise always resolves (no rejection): a failed load leaves the
 * existing cache entry untouched, the procedural fallback stays
 * active, and other in-flight loads in the batch continue.
 * Headless-safe: when `OffscreenCanvas` or `Image` is undefined the
 * load is a no-op.
 */
export function loadTerrainSpriteImage(
  kind: EnvironmentKind,
  url: string,
): Promise<void> {
  return new Promise((resolve) => {
    if (
      typeof OffscreenCanvas === 'undefined' ||
      typeof Image === 'undefined'
    ) {
      resolve();
      return;
    }
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      try {
        const canvas = new OffscreenCanvas(
          TERRAIN_SPRITE_SIZE,
          TERRAIN_SPRITE_SIZE,
        );
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          resolve();
          return;
        }
        ctx.clearRect(0, 0, TERRAIN_SPRITE_SIZE, TERRAIN_SPRITE_SIZE);
        ctx.drawImage(img, 0, 0, TERRAIN_SPRITE_SIZE, TERRAIN_SPRITE_SIZE);
        terrainSpriteCache.set(kind, canvas);
      } catch {
        /* leave cache untouched — procedural fallback stays active */
      }
      resolve();
    };
    img.onerror = () => resolve();
    img.src = url;
  });
}

/**
 * Per-entity sprite rotation per `TERRAIN_FEATURES.md §2.1`.
 * Golden-angle stride (≈ 2.39996 radians = 137.508°) keyed on
 * `entity.id` gives visual variety across instances without any RNG
 * dependency — deterministic, stable across save/load, distinct
 * between any two terrain entities in the same system.
 */
const GOLDEN_ANGLE_RADIANS = Math.PI * (3 - Math.sqrt(5));

export function spriteRotationForEntity(entityId: number): number {
  // Modulo 2π to keep the angle bounded; canvas `rotate` doesn't
  // require this but it keeps debug readouts sane.
  return (entityId * GOLDEN_ANGLE_RADIANS) % (Math.PI * 2);
}

/**
 * Test-only — clear the sprite cache. Called from unit tests so that
 * a load in one test doesn't leak into the next.
 */
export function _resetTerrainSpriteCacheForTests(): void {
  terrainSpriteCache.clear();
}
